<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { z } from 'zod'
  import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
  import BaseSelect from '@/components/base/BaseSelect.vue'
  import { DashboardReasonConfirmDialog } from '@/components/dashboard/common'
  import { useAxios } from '@/composables/useAxios'
  import {
    handleAxiosError,
    getErrorMessage,
    getBackendErrorCode
  } from '@/composables/useAxiosError'
  import { uuid } from '@/composables'
  import { useNotifyStore } from '@/stores'
  import {
    VServerSmtpDecisionResult,
    VServerSmtpRequestRow
  } from '@/types/dashboard/vserver/VServerSmtp'

  const { t, d } = useI18n()
  const notifyStore = useNotifyStore()

  type Action = 'approve' | 'deny' | 'block' | 'unblock'

  /** The API requires a note on these two and rejects them without one. */
  const NOTE_ACTIONS: Action[] = ['deny', 'block']

  const rows = ref<VServerSmtpRequestRow[]>([])
  const loading = ref(false)
  const busy = ref<number | null>(null)
  const statusFilter = ref('pending')

  // The list endpoint has no paging, only a limit capped at 200 (oldest
  // request first), so a full page means there may be more.
  const LIST_LIMIT = 200
  const truncated = computed(() => rows.value.length >= LIST_LIMIT)

  const dialogOpen = ref(false)
  const pendingAction = ref<{ action: Action; row: VServerSmtpRequestRow } | null>(null)

  const filterItems = computed(() =>
    ['pending', 'forced', 'approved', 'denied', 'none', 'all'].map((value) => ({
      value,
      title: t(`dashboard.vserver.admin.smtp.filter.${value}`)
    }))
  )

  async function load() {
    loading.value = true
    try {
      const res = await useAxios<never, VServerSmtpRequestRow[]>(
        'GET',
        `/admin/vserver/smtp/requests?status=${statusFilter.value}&limit=${LIST_LIMIT}`,
        true,
        undefined,
        z.array(VServerSmtpRequestRow)
      )
      rows.value = res.data
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'AdminVServerSmtpRequests' })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('dashboard.vserver.admin.smtp.loadError'),
        message: getErrorMessage(error)
      })
    } finally {
      loading.value = false
    }
  }

  function start(action: Action, row: VServerSmtpRequestRow) {
    if (NOTE_ACTIONS.includes(action)) {
      pendingAction.value = { action, row }
      dialogOpen.value = true
      return
    }
    void run(action, row)
  }

  const REFUSALS: Record<string, string> = {
    smtp_grandfathered: 'grandfathered',
    smtp_request_pending: 'requestPending',
    smtp_no_pending_request: 'noPendingRequest',
    smtp_forced: 'forced',
    smtp_not_blocked: 'notBlocked'
  }

  function refusalKey(error: unknown): string | undefined {
    const code = getBackendErrorCode(error)
    return code ? REFUSALS[code] : undefined
  }

  async function run(action: Action, row: VServerSmtpRequestRow, note?: string) {
    busy.value = row.account_id
    try {
      const res = await useAxios<{ note?: string }, VServerSmtpDecisionResult>(
        'POST',
        `/admin/vserver/smtp/requests/${row.account_id}/${action}`,
        true,
        note ? { note } : undefined,
        VServerSmtpDecisionResult
      )
      dialogOpen.value = false
      pendingAction.value = null
      // The decision is committed either way; a tag push that did not land
      // leaves the host on the old state until the next sync, so say so.
      const sync = res.data.tag_sync
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: sync === 'ok' ? 'success' : 'warning',
        title: t(`dashboard.vserver.admin.smtp.actions.${action}Success`),
        message: sync === 'ok' ? row.email : t(`dashboard.vserver.admin.smtp.tagSync.${sync}`)
      })
      await load()
    } catch (error) {
      const refusal = refusalKey(error)
      handleAxiosError(error, {
        logPrefix: 'AdminVServerSmtpRequests',
        silentStatuses: refusal ? [409] : []
      })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('dashboard.vserver.admin.smtp.actionError'),
        message: refusal
          ? t(`dashboard.vserver.admin.smtp.refusals.${refusal}`)
          : getErrorMessage(error)
      })
      // Each refusal means the row was out of date.
      if (refusal) {
        dialogOpen.value = false
        pendingAction.value = null
        await load()
      }
    } finally {
      busy.value = null
    }
  }

  function confirmNote(note: string) {
    const pending = pendingAction.value
    if (pending) void run(pending.action, pending.row, note)
  }

  /**
   * Approve works without a pending request too, for an unblock asked for by
   * mail, but only on a blocked account; a pending request stays approvable
   * after its block lapsed. Under a forced block it is refused until the block
   * is lifted, so it shows disabled with the reason.
   */
  function canOfferApprove(row: VServerSmtpRequestRow): boolean {
    return row.blocked || row.request_status === 'pending'
  }

  function formatDate(value?: string | null): string {
    return value ? d(new Date(value), 'short') : '—'
  }

  onMounted(load)
</script>

<template>
  <BaseDashboardPage
    icon="mdi-email-lock-outline"
    :title="t('dashboard.vserver.admin.smtp.title')"
    :subtitle="t('dashboard.vserver.admin.smtp.subtitle')"
    show-back
    :back-route="{ name: 'dashboardAdminVServer' }"
  >
    <div class="filter-bar">
      <BaseSelect
        v-model="statusFilter"
        :items="filterItems"
        item-title="title"
        item-value="value"
        :label="t('dashboard.vserver.admin.smtp.filter.label')"
        @update:model-value="load"
      />
    </div>

    <p v-if="loading" class="state-text">{{ t('common.loading') }}</p>
    <p v-else-if="rows.length === 0" class="state-text">
      {{ t('dashboard.vserver.admin.smtp.empty') }}
    </p>

    <p v-if="!loading && truncated" class="state-text truncated">
      {{ t('dashboard.vserver.admin.smtp.truncated', { n: LIST_LIMIT }) }}
    </p>

    <div v-if="!loading && rows.length > 0" class="request-list">
      <article v-for="row in rows" :key="row.account_id" class="request-card">
        <header class="request-header">
          <div>
            <h3>{{ row.name || row.email }}</h3>
            <p class="request-email">{{ row.email }} · #{{ row.account_id }}</p>
          </div>
          <div class="badges">
            <span v-if="row.forced" class="badge badge-forced">
              {{ t('dashboard.vserver.admin.smtp.forced') }}
            </span>
            <span v-if="row.grandfathered" class="badge">
              {{ t('dashboard.vserver.admin.smtp.grandfathered') }}
            </span>
            <span class="badge">{{
              t(`dashboard.vserver.admin.smtp.statusLabel.${row.request_status}`)
            }}</span>
          </div>
        </header>

        <dl class="signals">
          <div>
            <dt>{{ t('dashboard.vserver.admin.smtp.signals.age') }}</dt>
            <dd>{{ t('dashboard.vserver.admin.smtp.days', { n: row.age_days }) }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.vserver.admin.smtp.signals.opensAt') }}</dt>
            <dd>{{ row.grandfathered ? '—' : formatDate(row.opens_at) }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.vserver.admin.smtp.signals.vservers') }}</dt>
            <dd>{{ row.vserver_count }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.vserver.admin.smtp.signals.verified') }}</dt>
            <dd>{{ row.email_verified ? '✓' : '—' }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.vserver.admin.smtp.signals.payment') }}</dt>
            <dd>{{ row.has_payment_method ? '✓' : '—' }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.vserver.admin.smtp.signals.requested') }}</dt>
            <dd>{{ formatDate(row.requested_at) }}</dd>
          </div>
        </dl>

        <!-- Customer-written text, rendered as text, never v-html. -->
        <blockquote v-if="row.reason" class="reason">{{ row.reason }}</blockquote>
        <p v-if="row.decision_note" class="decision-note">
          {{ t('dashboard.vserver.admin.smtp.decisionNote', { note: row.decision_note }) }}
        </p>
        <p v-if="row.forced_reason" class="approve-blocked">
          {{ t('dashboard.vserver.admin.smtp.forcedReason', { reason: row.forced_reason }) }}
        </p>

        <p v-if="row.forced && canOfferApprove(row)" class="approve-blocked">
          {{ t('dashboard.vserver.admin.smtp.approveBlocked') }}
        </p>
        <p
          v-else-if="!row.forced && !row.grandfathered && row.request_status === 'pending'"
          class="approve-blocked block-blocked"
        >
          {{ t('dashboard.vserver.admin.smtp.refusals.requestPending') }}
        </p>

        <div class="actions">
          <button
            v-if="canOfferApprove(row)"
            class="act act-approve"
            :disabled="busy === row.account_id || row.forced"
            @click="start('approve', row)"
          >
            {{
              t(
                row.request_status === 'pending'
                  ? 'dashboard.vserver.admin.smtp.actions.approve'
                  : 'dashboard.vserver.admin.smtp.actions.approveWithoutRequest'
              )
            }}
          </button>
          <button
            v-if="row.request_status === 'pending'"
            class="act"
            :disabled="busy === row.account_id"
            @click="start('deny', row)"
          >
            {{ t('dashboard.vserver.admin.smtp.actions.deny') }}
          </button>
          <button
            v-if="row.forced"
            class="act"
            :disabled="busy === row.account_id"
            @click="start('unblock', row)"
          >
            {{ t('dashboard.vserver.admin.smtp.actions.unblock') }}
          </button>
          <!-- The terms limit a re-block to customers under the new rule; a
               grandfathered spammer gets the VServer lock instead. -->
          <button
            v-else-if="!row.grandfathered"
            class="act act-danger"
            :disabled="busy === row.account_id || row.request_status === 'pending'"
            @click="start('block', row)"
          >
            {{ t('dashboard.vserver.admin.smtp.actions.block') }}
          </button>
        </div>
      </article>
    </div>

    <DashboardReasonConfirmDialog
      v-model="dialogOpen"
      :variant="pendingAction?.action === 'block' ? 'danger' : 'warning'"
      :title="
        pendingAction ? t(`dashboard.vserver.admin.smtp.actions.${pendingAction.action}Title`) : ''
      "
      :description="
        pendingAction
          ? t('dashboard.vserver.admin.smtp.confirmFor', { email: pendingAction.row.email })
          : ''
      "
      :reason-label="t('dashboard.vserver.admin.smtp.noteLabel')"
      :reason-placeholder="t('dashboard.vserver.admin.smtp.notePlaceholder')"
      :min-length="10"
      :max-length="1000"
      @confirm="confirmNote"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
  .filter-bar {
    max-width: 260px;
    margin-bottom: 20px;
  }

  .state-text {
    color: rgba(200, 218, 255, 0.6);
    font-size: 0.9rem;
  }

  .request-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .request-card {
    background: rgba(15, 23, 48, 0.55);
    border: 1px solid rgba(112, 164, 255, 0.14);
    border-radius: 14px;
    padding: 18px 20px;
  }

  .request-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;

    h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.92);
    }
  }

  .request-email {
    margin: 2px 0 0 0;
    font-size: 0.78rem;
    color: rgba(200, 218, 255, 0.55);
  }

  .badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
  }

  .badge {
    flex-shrink: 0;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    background: rgba(112, 164, 255, 0.14);
    color: rgba(200, 218, 255, 0.85);

    &.badge-forced {
      background: rgba(244, 67, 54, 0.16);
      color: #ff8a80;
    }
  }

  .signals {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 10px 16px;
    margin: 0 0 14px 0;

    dt {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: rgba(200, 218, 255, 0.45);
    }

    dd {
      margin: 2px 0 0 0;
      font-size: 0.88rem;
      color: rgba(255, 255, 255, 0.88);
    }
  }

  .reason {
    margin: 0 0 12px 0;
    padding: 10px 14px;
    border-left: 3px solid rgba(112, 164, 255, 0.4);
    border-radius: 0 8px 8px 0;
    background: rgba(255, 255, 255, 0.04);
    font-size: 0.85rem;
    line-height: 1.55;
    color: rgba(200, 218, 255, 0.85);
    white-space: pre-wrap;
  }

  .decision-note {
    margin: 0 0 12px 0;
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.7);
    white-space: pre-wrap;
  }

  .approve-blocked {
    margin: 0 0 12px 0;
    font-size: 0.78rem;
    color: rgba(255, 138, 128, 0.9);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .act {
    padding: 7px 14px;
    border: 1px solid rgba(112, 164, 255, 0.22);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.04);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
    cursor: pointer;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    &.act-approve {
      background: rgba(29, 233, 182, 0.16);
      border-color: rgba(29, 233, 182, 0.3);
    }
    &.act-danger {
      background: rgba(244, 67, 54, 0.14);
      border-color: rgba(244, 67, 54, 0.28);
    }
  }
</style>

<style lang="scss">
  /* Light theme, unscoped per the repo's Vuetify styling rule. */
  .v-theme--lightTheme {
    .request-card {
      background: rgba(15, 23, 42, 0.03);
      border-color: rgba(15, 23, 42, 0.1);
    }
    .request-header h3 {
      color: rgba(15, 23, 42, 0.9);
    }
    .request-email,
    .state-text,
    .signals dt {
      color: rgba(15, 23, 42, 0.55);
    }
    .signals dd {
      color: rgba(15, 23, 42, 0.85);
    }
    .reason {
      background: rgba(15, 23, 42, 0.04);
      color: rgba(15, 23, 42, 0.8);
    }
    .act {
      color: rgba(15, 23, 42, 0.85);
      background: rgba(15, 23, 42, 0.04);
    }
  }
</style>
